import { driver } from 'driver.js';
import { useEffect, useMemo } from 'react';
import { useTutorialContext } from '../contexts/TutorialContext';

const areElementsRendered = ({ ids = [], withinId }) => {
  if (ids.length === 0 || !withinId) return true;
  return ids.every(id => document.getElementById(id) !== null);
};

export const usePageTutorial = ({ steps, feature, disable, waitForElements = {}, config = {} }) => {
  const { tutorial, setTutorial, setSeenNewFeature, setEnabled } = useTutorialContext();

  useEffect(() => {
    setEnabled(true);

    return () => {
      setEnabled(false);
    };
  }, [setEnabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useMemo, will refactor later
  const pageTutorialDriver = useMemo(
    () =>
      driver({
        ...config,
        showProgress: true,
        onDestroyed: () => {
          setTutorial(false);
          feature && setSeenNewFeature(feature);
          config.onDestroyed?.();
        },
        steps,
      }),
    [steps, config, setTutorial],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy useEffect, will refactor later
  useEffect(() => {
    if (!disable && tutorial) {
      if (areElementsRendered(waitForElements)) {
        pageTutorialDriver.drive();
      } else {
        const rendered = waitForElements.ids.map(() => false);

        const observer = new MutationObserver(mutations => {
          mutations.forEach(m => {
            if (m.addedNodes.length > 0) {
              m.addedNodes.forEach(node => {
                const index = waitForElements.ids.findIndex(v => v === node.id);
                if (index !== -1) {
                  rendered[index] = true;
                }
              });
            } else if (m.nextSibling?.id) {
              const index = waitForElements.ids.findIndex(v => v === m.nextSibling.id);
              if (index !== -1) {
                rendered[index] = true;
              }
            }
          });
          if (rendered.every(v => v)) {
            observer.disconnect();
            pageTutorialDriver.drive();
          }
        });

        observer.observe(document.getElementById(waitForElements.withinId), {
          childList: true,
          subtree: false,
          attributes: false,
          characterData: false,
        });
      }
    }
  }, [disable, tutorial]);
};
